// skipcq: JS-W1028
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  captureBeerIngestionSnapshotEmpty,
  captureBeerIngestionSnapshotError,
  captureBeerIngestionSnapshotFilter,
} from '../../utils/beerIngestionAnalytics';
import { canRetryIngestionRun } from '../../utils/brewery/beerIngestionRunFilters';
import {
  filterSnapshotBeers,
  SNAPSHOT_FILTER_ALL,
  SNAPSHOT_FILTER_FLAGGED,
  SNAPSHOT_FILTER_INVALID,
  SNAPSHOT_FILTER_VALID,
  snapshotBeerStatus,
} from '../../utils/brewery/beerIngestionSnapshot';
import BeerIngestionSnapshotTable from './BeerIngestionSnapshotTable';

const PENDING_RETRY_COPY = 'This run has raw extract data but no validated snapshot yet. Retry processing to build one.';
const PENDING_NO_RETRY_COPY = 'This run has no validated snapshot.';
const EMPTY_EXTRACT_COPY = 'No beers were extracted from this source.';
const FOOTNOTE_COPY = 'Invalid and flagged rows cannot be approved as-is. A later review step can approve them after an edit. Missing ABV and 0% ABV are allowed.';

function SnapshotSummaryChips({ beers }) {
  const validCount = beers.filter((beer) => snapshotBeerStatus(beer) === 'valid').length;
  const flaggedCount = beers.filter((beer) => snapshotBeerStatus(beer) === 'flagged').length;
  const invalidCount = beers.filter((beer) => snapshotBeerStatus(beer) === 'invalid').length;
  const missingAbvCount = beers.filter((beer) => (
    beer.issues?.some((issue) => issue.code === 'missing_abv')
  )).length;

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
      <Chip size="small" color="success" label={`Valid ${validCount}`} />
      <Chip size="small" color="warning" label={`Flagged ${flaggedCount}`} />
      <Chip size="small" color="error" label={`Invalid ${invalidCount}`} />
      <Chip size="small" variant="outlined" label={`Missing ABV ${missingAbvCount}`} />
    </Box>
  );
}

SnapshotSummaryChips.propTypes = {
  beers: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default function BeerIngestionSnapshotPanel({
  run,
  snapshot,
  breweryId,
}) {
  const [filter, setFilter] = useState(SNAPSHOT_FILTER_ALL);
  const beers = useMemo(
    () => (Array.isArray(snapshot?.beers) ? snapshot.beers : []),
    [snapshot],
  );
  const filteredBeers = useMemo(() => filterSnapshotBeers(beers, filter), [beers, filter]);

  useEffect(() => {
    if (!run || run.runKind !== 'extraction') {
      return;
    }
    if (snapshot && beers.length === 0) {
      captureBeerIngestionSnapshotEmpty(run.id, breweryId);
      return;
    }
    if (!snapshot && run.hasValidatedSnapshot) {
      captureBeerIngestionSnapshotError(run.id, breweryId);
    }
  }, [run, snapshot, beers.length, breweryId]);

  const handleFilterChange = useCallback((unusedEvent, nextFilter) => {
    if (!nextFilter || !run) {
      return;
    }
    setFilter(nextFilter);
    captureBeerIngestionSnapshotFilter(run.id, breweryId, nextFilter);
  }, [run, breweryId]);

  if (!run || run.runKind !== 'extraction') {
    return null;
  }

  if (run.status === 'failure' && !snapshot) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Extraction did not produce a snapshot
        {run.hasSnapshot ? '; retry is available when raw beers are stored.' : '.'}
      </Typography>
    );
  }

  if (!snapshot) {
    const retryAllowed = canRetryIngestionRun(run);
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {retryAllowed ? PENDING_RETRY_COPY : PENDING_NO_RETRY_COPY}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Extracted beers</Typography>
      {beers.length === 0 ? (
        <Alert severity="info" sx={{ mb: 1 }}>{EMPTY_EXTRACT_COPY}</Alert>
      ) : null}
      <SnapshotSummaryChips beers={beers} />
      <ToggleButtonGroup
        exclusive
        size="small"
        value={filter}
        onChange={handleFilterChange}
        aria-label="Snapshot row filter"
        sx={{ mb: 1 }}
      >
        <ToggleButton value={SNAPSHOT_FILTER_ALL}>All</ToggleButton>
        <ToggleButton value={SNAPSHOT_FILTER_VALID}>Valid</ToggleButton>
        <ToggleButton value={SNAPSHOT_FILTER_FLAGGED}>Flagged</ToggleButton>
        <ToggleButton value={SNAPSHOT_FILTER_INVALID}>Invalid</ToggleButton>
      </ToggleButtonGroup>
      {beers.length > 0 ? <BeerIngestionSnapshotTable beers={filteredBeers} /> : null}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        {FOOTNOTE_COPY}
      </Typography>
    </Box>
  );
}

BeerIngestionSnapshotPanel.propTypes = {
  run: PropTypes.object,
  snapshot: PropTypes.object,
  breweryId: PropTypes.string,
};
